// @erp/ui/qr-code — an SVG QR renderer over `qrcode-generator` (MIT, zero deps,
// the same encoder the shippra source used). SVG rather than canvas so it scales
// crisply, prints, and needs no ref/paint lifecycle.
//
// Colors come from the daisyUI theme, so the code inverts correctly in dark mode
// — a canvas with hardcoded black-on-white would be unreadable there.
import { For, createMemo } from "solid-js";
import qrcode from "qrcode-generator";

export function QrCode(props: {
  value: string;
  /** Rendered pixel size (square). */
  size?: number;
  /** Error-correction level. 'M' tolerates ~15% damage — right for a screen. */
  level?: "L" | "M" | "Q" | "H";
  /** Quiet-zone margin, in module units. The spec asks 4; 2 is enough on screen. */
  margin?: number;
  /** Module color (CSS value). Default: the theme's base-content — a PRINT or
   *  card surface passes pure black (`#000000`: symbologies want the maximum
   *  module contrast a scanner can read; modules/lib's print palette names it
   *  `PRINT.code`), never a gray stop and never a theme var. */
  fg?: string;
  /** Background color (CSS value). Default: the theme's base-100. */
  bg?: string;
  /** Accessible name. Defaults to "QR code". */
  label?: string;
  /** Utility classes on the <svg> — e.g. "h-auto max-w-full" so a fixed `size`
   *  can still shrink inside a narrow phone viewport instead of forcing the
   *  track wider than the screen. */
  class?: string;
  id?: string;
}) {
  const model = createMemo(() => {
    // typeNumber 0 = pick the smallest version that fits the payload.
    const qr = qrcode(0, props.level ?? "M");
    qr.addData(props.value || " ");
    qr.make();
    const count = qr.getModuleCount();
    const cells: { r: number; c: number }[] = [];
    for (let r = 0; r < count; r++) {
      for (let c = 0; c < count; c++) if (qr.isDark(r, c)) cells.push({ r, c });
    }
    return { count, cells };
  });

  const margin = () => props.margin ?? 2;
  const span = () => model().count + margin() * 2;

  return (
    <svg
      id={props.id}
      class={props.class}
      width={props.size ?? 176}
      height={props.size ?? 176}
      viewBox={`0 0 ${span()} ${span()}`}
      shape-rendering="crispEdges"
      role="img"
      aria-label={props.label ?? "QR code"}
    >
      {props.bg ? (
        <rect width={span()} height={span()} fill={props.bg} />
      ) : (
        <rect width={span()} height={span()} class="fill-base-100" />
      )}
      <For each={model().cells}>
        {(cell) =>
          props.fg ? (
            <rect x={cell.c + margin()} y={cell.r + margin()} width="1" height="1" fill={props.fg} />
          ) : (
            <rect x={cell.c + margin()} y={cell.r + margin()} width="1" height="1" class="fill-base-content" />
          )
        }
      </For>
    </svg>
  );
}
